import React, { useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MainApp from './components/MainApp';
import Loading from './components/Loading';
import {
  clearAllStorage,
  initializeSession,
  getHashSiteId,
  setHashSiteId,
  setApiKey,
  setSettings,
  setEmbedContext,
} from './utils/storage';
import { apiFetch } from './utils/api';
import { userFacingError } from './utils/errors';
import i18n, { SUPPORTED_LANGS } from './i18n';
import './App.css';
import './vault.css';

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initializingRef = useRef(false);

  async function loadApiKeyAndSettings(hashSiteId) {
    const data = await apiFetch('/api/settings', {}, { id: hashSiteId });
    if (data?.settings?.apiKey) setApiKey(data.settings.apiKey);
    if (data?.apiKey) setApiKey(data.apiKey);
    if (data?.settings) setSettings(data.settings);
    return data;
  }

  async function loadStoreInfo(hashSiteId) {
    try {
      const data = await apiFetch('/api/store-info', {}, { id: hashSiteId });
      setStoreInfo(data);
    } catch {
      // Non-blocking — vault still works without profile chrome.
    }
  }

  async function finishAuth(hashSiteId, embedExtras = {}) {
    setHashSiteId(hashSiteId);
    if (Object.keys(embedExtras).length) setEmbedContext(embedExtras);
    const embedLang = String(embedExtras.lang || '')
      .split(/[-_]/)[0]
      .toLowerCase();
    if (embedLang && SUPPORTED_LANGS.includes(embedLang)) {
      await i18n.changeLanguage(embedLang);
    }
    initializeSession();
    await loadApiKeyAndSettings(hashSiteId);
    setIsAuthenticated(true);
    await loadStoreInfo(hashSiteId);
    setLoading(false);
  }

  useEffect(() => {
    const initialize = async () => {
      if (initializingRef.current) return;
      initializingRef.current = true;

      const params = new URLSearchParams(window.location.search);
      const payloadFromUrl = params.get('payload');
      const id = params.get('id');
      const storedHashId = getHashSiteId();
      const isEmbedded = window.self !== window.top && typeof window.EcwidApp !== 'undefined';

      try {
        if (isEmbedded && window.EcwidApp) {
          const app_state = params.get('app_state') || undefined;
          const body = {};
          if (app_state) body.app_state = app_state;
          if (payloadFromUrl) {
            body.payload = payloadFromUrl;
          } else {
            const payload = window.EcwidApp.getPayload();
            if (payload?.store_id && payload?.access_token) {
              body.store_id = payload.store_id;
              body.access_token = payload.access_token;
              if (payload.lang != null) body.lang = payload.lang;
              if (payload.view_mode != null) body.view_mode = payload.view_mode;
            }
          }

          if ((body.store_id && body.access_token) || body.payload) {
            const data = await apiFetch('/api/embed-auth', {
              method: 'POST',
              body: JSON.stringify(body),
            });
            if (data.hashSiteId) {
              const extras = {};
              if (data.lang != null) extras.lang = data.lang;
              if (data.view_mode != null) extras.view_mode = data.view_mode;
              if (data.app_state != null) extras.app_state = data.app_state;
              await finishAuth(data.hashSiteId, extras);
              return;
            }
          }
          setError(t('app.openFromControlPanel'));
          setLoading(false);
          return;
        }

        if (id) {
          await finishAuth(id);
          return;
        }

        if (storedHashId) {
          await finishAuth(storedHashId);
          return;
        }

        setLoading(false);
      } catch (err) {
        setError(userFacingError(err, t('app.signInFailed')));
        setLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot bootstrap
  }, []);

  const handleLogout = () => {
    clearAllStorage();
    setIsAuthenticated(false);
    setStoreInfo(null);
    const base = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '');
    window.location.href = base ? `${base}/install` : '/install';
  };

  const toaster = (
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 4000,
      }}
    />
  );

  if (loading) {
    return (
      <>
        {toaster}
        <Loading message={t('app.loading')} />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {toaster}
        <div className="auth-gate">
          <aside className="auth-gate-hero" aria-hidden="true">
            <img
              className="auth-gate-hero-logo"
              src="/backuply-logo-long.png"
              alt=""
              width={200}
              height={42}
            />
            <div>
              <h1>{t('app.auth.heroTitle')}</h1>
              <p>{t('app.auth.heroBody')}</p>
              <ul className="auth-feature-list">
                <li>
                  <span className="auth-feature-icon">✓</span>
                  {t('app.auth.featureNightly')}
                </li>
                <li>
                  <span className="auth-feature-icon">✓</span>
                  {t('app.auth.featureNamed')}
                </li>
                <li>
                  <span className="auth-feature-icon">✓</span>
                  {t('app.auth.featureRestore')}
                </li>
              </ul>
            </div>
          </aside>
          <div className="auth-gate-panel">
            <div className="auth-card">
              <img
                className="auth-logo"
                src="/backuply-logo-long.png"
                alt="Backuply"
                width={220}
                height={47}
              />
              <p>{error || t('app.auth.openOrInstall')}</p>
              <div className="auth-card-actions">
                <a
                  className="btn btn-primary btn-vault-primary"
                  href={`${(import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '')}/install`}
                >
                  {t('app.auth.install')}
                </a>
              </div>
              <p className="auth-card-note">{t('app.auth.alreadyInstalled')}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {toaster}
      <MainApp storeInfo={storeInfo} onLogout={handleLogout} />
    </>
  );
}

export default App;
